import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormValidation } from '@pattern-library/base';
import { Button, FlexButtonGroup } from '@pattern-library/buttons';
import PasswordReveal from '@pattern-library/password-reveal';
import withLiveValidation from '@pattern-library/with-live-validation';
import { isLengthInRange } from '@pattern-library/base/dist/js/helpers/formValidationMethods';
import { matchesPattern, meetsAtLeastN } from './validation';

const propTypes = {
    formAction: PropTypes.string.isRequired,
    antiForgeryToken: PropTypes.string.isRequired,
    buttonProps: PropTypes.objectOf(PropTypes.string).isRequired,
    newPasswordInputProps: PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ).isRequired,
    confirmPasswordInputProps: PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ).isRequired,
    passwordRequirements: PropTypes.shape({
        minLength: PropTypes.number.isRequired,
        maxLength: PropTypes.number.isRequired
    }).isRequired
};

const LivePasswordReveal = withLiveValidation(PasswordReveal);

function CreatePasswordForm(props) {
    const {
        formAction,
        antiForgeryToken,
        buttonProps,
        newPasswordInputProps,
        confirmPasswordInputProps,
        passwordRequirements
    } = props;
    const { minLength, maxLength } = passwordRequirements;

    /** @type {import('react').MutableRefObject<HTMLFormElement>} */
    const formRef = useRef();
    const [customErrorMessage, setCustomErrorMessage] = useState(null);

    const schema = {
        [newPasswordInputProps.name]: [
            {
                validationMethod: value => isLengthInRange(value, minLength, maxLength),
                validationLabel: `${minLength} to ${maxLength} characters`,
                ariaLabel: `Must be ${minLength} to ${maxLength} characters`,
                errorMessage: 'Password does not meet requirements'
            },
            {
                validationMethod: meetsAtLeastN(3, [
                    matchesPattern(/[a-z]/), // lowercase letter
                    matchesPattern(/[A-Z]/), // uppercase letter
                    matchesPattern(/[0-9]/), // number
                    matchesPattern(/[^a-zA-Z0-9]/) // special character
                ]),
                validationLabel:
                    'At least 3 of the following: lowercase letter, uppercase letter, number, special character',
                ariaLabel:
                    'Must have at least 3 of the following: lowercase letter, uppercase letter, number, special character',
                errorMessage: 'Password does not meet requirements'
            }
        ],
        [confirmPasswordInputProps.name]: [
            {
                validationMethod: value => {
                    const otherInput = formRef.current.elements[newPasswordInputProps.name];
                    return otherInput?.value === value;
                },
                errorMessage: 'Passwords do not match'
            }
        ]
    };

    const {
        hasError,
        errorMessage,
        onSubmit: onSubmitHook,
        onChange
    } = useFormValidation({ schema });

    const invalidSymbolErrorMessage =
        'Invalid symbol entered. Symbols allowed: @ # $ % ^ & * - _ ! + = [ ] { } | \\ : \' , . ? / ` ~ " ( ) ; < >';
    const invalidSymbolAriaLabel = `Invalid symbol entered. Symbols allowed: at sign, number sign, dollar sign, percentage sign, caret symbol,
    ampersand, asterisk, hyphen, underscore, exclamation mark, plus sign, equals sign, left and right square brackets, 
    left and right curly brackets, pipe, backslash, colon, apostrophe, comma, period, question mark, forward slash, 
    backtick, tilde, quotation mark, left and right parenthesis, semi-colon, less-than sign, greater-than sign.`;

    const onSubmit = event => {
        const passwordValue = formRef.current.elements[newPasswordInputProps.name].value;
        // Regex to match allowed symbols and alphanumeric characters
        // eslint-disable-next-line
        const regex = /^[a-zA-Z0-9@#$%^&*-_!+=\[\]{}|\\:'`,.?/~"();<>]*$/;

        if (!regex.test(passwordValue)) {
            event.preventDefault();
            setCustomErrorMessage(
                <>
                    <span aria-hidden="true">{invalidSymbolErrorMessage}</span>
                    <span className="asc-acc-hidden">{invalidSymbolAriaLabel}</span>
                </>
            );
        } else {
            setCustomErrorMessage(null);
        }

        onSubmitHook(event);
    };

    return (
        <form
            ref={formRef}
            action={formAction}
            method="post"
            onChange={onChange}
            onSubmit={onSubmit}
            autoComplete="off"
            noValidate
        >
            <div dangerouslySetInnerHTML={{ __html: antiForgeryToken }} />
            <LivePasswordReveal
                criteria={schema[newPasswordInputProps.name]}
                isRequired
                preventAutofill
                {...newPasswordInputProps}
                hasError={customErrorMessage !== null || hasError(newPasswordInputProps.name)}
                errorMessage={customErrorMessage || errorMessage(newPasswordInputProps.name)}
            />

            <PasswordReveal
                containerClasses="asc-mt-3"
                isRequired
                preventAutofill
                {...confirmPasswordInputProps}
                hasError={hasError(confirmPasswordInputProps.name)}
                errorMessage={errorMessage(confirmPasswordInputProps.name)}
            />

            <FlexButtonGroup alignment="center" className="asc-mt-4 asc-mb-0">
                <Button type="submit" className="asc-mtb-0" {...buttonProps} />
            </FlexButtonGroup>
        </form>
    );
}

CreatePasswordForm.propTypes = propTypes;
export default CreatePasswordForm;
